import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BdCompanyContextService,
  BdGrantsService,
  ShareableGrantFull,
} from '@bindoc-projects/bindoc-permissions-lib/data';
import { ShareableType } from '@bindoc-projects/resource-domain-lib';
import { BD_DANGER_MENU_ITEM_CSS } from '../../info-menu/danger-menu-item';
import { BdConfirmDeleteService } from '@bindoc-projects/ui-lib/dialog/ui';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { Breadcrumb } from 'primeng/breadcrumb';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { skip, take } from 'rxjs';

import { BdFolderTableComponent } from '../folder-table/folder-table.component';
import { BdGrantActionsService } from '../group-shares/grant-actions.service';
import { grantToFolderItem } from '../group-shares/grant-to-folder-item';
import {
  FolderItem,
  isSameFolderItem,
  ItemNavigateEvent,
  SHAREABLE_LIST_BROWSE,
  ShareableListPresentation,
} from '../shareables-overview.models';
import {
  BdSharedFolderNavigator,
  SHARED_FOLDER_BREADCRUMB_CSS,
} from '../shared-folder/shared-folder-navigator';

@Component({
  selector: 'bd-grants-tab',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (folders.isOpen()) {
      <p-breadcrumb
        class="block mb-3"
        data-test="shared-folder-breadcrumb"
        [model]="folders.breadcrumbItems()"
        [home]="folders.breadcrumbHome"
      ></p-breadcrumb>
    }
    <!--
      The Typ filter is safe here because grants arrive unpaginated, so the
      table sees the whole list. It is off while "selection" is controlled: the
      resource picker embeds this tab and already filters by type itself.
    -->
    <bd-folder-table
      [items]="rows()"
      [loading]="folders.isOpen() ? folders.loading() : loading()"
      [notice]="folders.notice()"
      [presentation]="presentation()"
      [selection]="selection()"
      [showType]="showType()"
      [typeFilter]="selection() === undefined"
      [sortMode]="'client'"
      [menuItemsProvider]="menuItemsProvider"
      [emptyMessage]="
        folders.isOpen() ? 'Dieser Ordner ist leer' : 'Keine geteilten Einträge'
      "
      (itemClick)="onItemClick($event)"
      (selectionChange)="onTableSelectionChange($event)"
    ></bd-folder-table>
    <p-confirmdialog />
  `,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        min-height: 0;
      }
    `,
    BD_DANGER_MENU_ITEM_CSS,
    SHARED_FOLDER_BREADCRUMB_CSS,
  ],
  imports: [BdFolderTableComponent, Breadcrumb, ConfirmDialog],
  providers: [
    ConfirmationService,
    BdConfirmDeleteService,
    BdGrantActionsService,
    BdSharedFolderNavigator,
  ],
})
export class BdGrantsTabComponent implements OnInit {
  public readonly shareableType = input<ShareableType | undefined>(undefined);
  /**
   * Multi-type filter; takes precedence over {@link shareableType} when set.
   * Applied client-side — grants come back unpaginated and the endpoint has no
   * type parameter.
   */
  public readonly shareableTypes = input<readonly ShareableType[] | undefined>(
    undefined,
  );
  /** Show a "Typ" column — useful when more than one shareable type is listed. */
  public readonly showType = input(false);
  /**
   * Browse or pick — see {@link ShareableListPresentation}. Forwarded to the
   * table as it is (`showLabelFilter` has no counterpart here). With
   * `showCheckbox`, row clicks toggle the selection instead of navigating (see
   * {@link onItemClick}).
   */
  public readonly presentation = input<ShareableListPresentation>(
    SHAREABLE_LIST_BROWSE,
  );
  /**
   * Forwarded to {@link BdFolderTableComponent.selection} — set it to control
   * the ticks from outside (a picker whose selection outlives a reload).
   */
  public readonly selection = input<readonly FolderItem[] | undefined>(
    undefined,
  );
  public readonly itemNavigate = output<ItemNavigateEvent>();
  public readonly selectionChange = output<FolderItem[]>();

  public readonly loading = signal(false);

  /**
   * Browsing into a shared folder without leaving this list (BDA-6454). The
   * open folder is the workspace location's — set by link or by click alike.
   */
  protected readonly folders = inject(BdSharedFolderNavigator);

  /** Every grant the API returned, before the type filter. */
  private readonly _allGrants = signal<ShareableGrantFull[]>([]);

  /**
   * The rows to show. Because grants arrive unpaginated, the type filter is a
   * pure derivation: narrowing it takes effect without a refetch — and so cannot
   * race one.
   */
  public readonly grantItems = computed<FolderItem[]>(() => {
    const types = this._effectiveTypes();
    const grants = types
      ? this._allGrants().filter((g) =>
          types.includes(g.shareableReference.type),
        )
      : this._allGrants();
    return grants.map((g) => grantToFolderItem(g));
  });

  /**
   * What the table shows: the shares, or the contents of the shared folder that
   * is open. Inside a folder the type filter narrows files only — folders are
   * the way further down.
   */
  public readonly rows = computed<FolderItem[]>(() => {
    if (!this.folders.isOpen()) {
      return this.grantItems();
    }
    const types = this._effectiveTypes();
    const items = this.folders.items();
    return types
      ? items.filter((i) => i.isFolder || types.includes(i.type))
      : [...items];
  });

  private readonly _grantsService = inject(BdGrantsService);
  private readonly _companyContext = inject(BdCompanyContextService);
  private readonly _grantActions = inject(BdGrantActionsService);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _grantsByItemId = new Map<string, ShareableGrantFull>();

  private readonly _folderTable = viewChild(BdFolderTableComponent);

  public readonly menuItemsProvider = (item: FolderItem): MenuItem[] =>
    this.folders.isOpen()
      ? this.presentation().showActions
        ? this.folders.menuItems(item, (row) => this.onItemClick(row))
        : []
      : this._grantMenuItems(item);

  private readonly _grantMenuItems = (item: FolderItem): MenuItem[] =>
    this._grantActions.menuItems(item, {
      grant: this._grantsByItemId.get(item.id),
      showActions: this.presentation().showActions,
      onOpen: (row) => this.onItemClick(row),
      onLabelsChanged: (row, labels) =>
        this._allGrants.update((grants) =>
          grants.map((g) =>
            isSameFolderItem(g.shareableReference, row)
              ? {
                  ...g,
                  shareableReference: { ...g.shareableReference, labels },
                }
              : g,
          ),
        ),
      onRevoked: () => this.reload(),
    });

  ngOnInit(): void {
    this.reload();
    // Another company shares other things. The navigator takes an open folder
    // back to the list; the list itself is this tab's to re-read.
    this._companyContext.selectedCompanyId$
      .pipe(skip(1), takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this.reload());
  }

  private _sharedFolderIds(): ReadonlySet<string> {
    return new Set(
      this._allGrants()
        .filter((g) => g.shareableReference.type === ShareableType.FOLDER)
        .map((g) => g.shareableReference.id),
    );
  }

  /**
   * `shareableTypes` (multi-type) takes precedence over the single-type
   * `shareableType` input when both are set.
   */
  private _effectiveTypes(): ShareableType[] | undefined {
    const multi = this.shareableTypes();
    if (multi) {
      return [...multi];
    }
    const single = this.shareableType();
    return single ? [single] : undefined;
  }

  public reload(): void {
    this.loading.set(true);
    const companyId = this._companyContext.currentCompanyId;
    this._grantsService
      .myGrants('READ', companyId)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          const grants: ShareableGrantFull[] = Array.isArray(response)
            ? response
            : ((response as { content: ShareableGrantFull[] }).content ?? []);
          // Keyed by the full set, not the filtered rows: the map backs
          // "Freigabe entfernen", and the type filter is applied downstream.
          //
          // A resource can reach the current user through more than one grant
          // (e.g. directly and via a group) — keep only the first grant per
          // resource so it isn't listed as duplicate, otherwise-identical rows
          // (BDA-5971).
          this._grantsByItemId.clear();
          const uniqueGrants: ShareableGrantFull[] = [];
          grants.forEach((g) => {
            const id = g.shareableReference.id;
            if (!this._grantsByItemId.has(id)) {
              this._grantsByItemId.set(id, g);
              uniqueGrants.push(g);
            }
          });
          this._allGrants.set(uniqueGrants);
          this.loading.set(false);
          // A shared folder's path starts at the folder this list shares.
          this.folders.setSharedFolderIds(this._sharedFolderIds());
        },
        error: () => {
          this._grantsByItemId.clear();
          this._allGrants.set([]);
          this.loading.set(false);
          this.folders.setSharedFolderIds(new Set());
        },
      });
  }

  public onItemClick(item: FolderItem): void {
    // A folder is navigation, also in picker mode: open it here, in the list
    // it was shared in, instead of jumping into the grantor's tree.
    if (item.isFolder) {
      this.folders.open(item);
      return;
    }
    if (this.presentation().showCheckbox) {
      // Row-click toggle for checkbox mode. The table owns the merge and the
      // controlled/uncontrolled split, so this is the same interaction as
      // clicking the row's checkbox.
      this._folderTable()?.toggle(item);
      return;
    }
    this.itemNavigate.emit({ id: item.id, type: item.type, name: item.name });
  }

  /**
   * Selection change coming from the table, which reports the complete selection
   * — including, under a controlled {@link selection}, the picks made in another
   * scope. Nothing left to do here but hand it on.
   */
  public onTableSelectionChange(items: FolderItem[]): void {
    this.selectionChange.emit(items);
  }
}
